// Explicit include expansion helpers (no implicit/default injection)

/**
 * Extract the first-line prologue and return version and body remainder.
 * Expects the text to begin with a %CADET-x.y line.
 */
export function stripPrologue(text: string): { version: string; body: string } {
  const re = /^\s*%CADET-(\d+)\.(\d+)[ \t]*(?:;)?[ \t]*(?:\r?\n|$)/;
  const match = re.exec(text);
  if (!match) {
    throw new Error('Missing %CADET-x.y prologue');
  }
  const version = `${match[1]}.${match[2]}`;
  const body = text.slice(match[0].length);
  return { version, body };
}

function sameMajor(a: string, b: string): boolean {
  return a.split('.')[0] === b.split('.')[0];
}

/**
 * Expand declared includes (symbol → CADET-DSL text) after the user prologue, returning combined text and line ID mapping.
 * Include-origin lines are tagged as `inc-<symbol>-<n>`.
 */
export function injectIncludesWithMap(
  userText: string,
  includes: readonly { symbol: string; text: string }[],
  opts?: { enforceSameMajor?: boolean }
): { text: string; lineIds: string[] } {
  const enforce = opts?.enforceSameMajor ?? true;
  const re = /^\s*%CADET-(\d+)\.(\d+)[ \t]*(?:;)?[ \t]*/;
  const userMatch = re.exec(userText);
  if (!userMatch) {
    throw new Error('User text missing %CADET-x.y prologue');
  }
  const userVersion = `${userMatch[1]}.${userMatch[2]}`;

  const prologueLine = userMatch[0].replace(/\r?\n$/, '');
  const remainder = userText.slice(userMatch[0].length);

  const parts: string[] = [];
  const lineIds: string[] = [];

  // user prologue line
  parts.push(prologueLine);
  lineIds.push('1');

  // blank line after prologue
  parts.push('');
  lineIds.push('2');

  let nextUserLine = 3;

  for (const include of includes) {
    const { version, body } = stripPrologue(include.text);
    if (enforce && !sameMajor(version, userVersion)) {
      throw new Error(
        `Include ${include.symbol} version ${version} is incompatible with user version ${userVersion}`
      );
    }
    if (body.length === 0) continue;
    const lines = body.replace(/\r\n/g, '\n').split('\n');
    let n = 0;
    for (const ln of lines) {
      if (ln.trim().length === 0) continue;
      n += 1;
      parts.push(ln);
      lineIds.push(`inc-${include.symbol}-${n}`);
    }
  }

  if (includes.length > 0) {
    parts.push('');
    lineIds.push(String(nextUserLine));
    nextUserLine++;
  }

  const cleanedRemainder = remainder.replace(/^\s+/, '');
  const userAfterRaw = cleanedRemainder.replace(/\r\n/g, '\n').split('\n');
  const userAfter =
    userAfterRaw.length > 0 && userAfterRaw[userAfterRaw.length - 1] === ''
      ? userAfterRaw.slice(0, -1)
      : userAfterRaw;
  for (const line of userAfter) {
    parts.push(line);
    lineIds.push(String(nextUserLine));
    nextUserLine++;
  }

  const text = parts.join('\n');
  return { text, lineIds };
}
